import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';

import { Button } from 'components/buttons';
import { FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { ShowHide } from 'components/layout';
import { NotificationToaster } from 'components/notifications';
import { EmployeeCircleStatus } from 'components/status';
import { Tooltip } from 'components/tooltip';

import { useDebounce } from 'hooks/useDebounce';
import { useRestrictedAccountIds } from 'hooks/useRestrictedAccountIds';
import { GlobalSearchService } from 'services';

const employeeEntityType = "employee";
const debounceTimeout = 750;
const minSearchLength = 3;
const defaultPageSize = 25;
const firstPage = 1;
const searchErrorMessage = "Could not search for drivers. Please try again.";
const noResultsHeading = "No drivers match that search.";
const noResultsMessage = "Try a different name, reference or card number.";

const tableHeaders = [
    "Driver",
    "Reference",
    "Card number",
    "Account",
    "Depot",
    ""
];

const defaultArray = [];
const defaultFunction = () => null;

// Worded as the alert listing words it, so the same driver reads the same way on both screens.
export function buildStatusTooltip(driver) {
    return `${driver?.status?.name} ${driver?.type?.name?.toLowerCase()} driver`;
}

// The search state is owned by the page rather than this component so an operator who collapses the
// search to a selected driver still has their list when they come back to it.
export const emptyDriverSearch = {
    term: "",
    searchedTerm: null,
    results: defaultArray,
    totalCount: 0,
    pageNumber: firstPage,
    pageSize: defaultPageSize,
    hasSearched: false,
    restrictedAccountIds: null
};

// One search result is one employee on one account, so a driver who spans accounts appears once per
// account and picking a row settles both identifiers. Nothing here switches the operator's session.
export function RulesEngineEmployeeSearch(
    {
        search = emptyDriverSearch,
        disabled = false,
        onSearchChange = defaultFunction,
        onDriverSelect = defaultFunction
    }
) {
    const [searching, setSearching] = useState(false);

    // Typing keeps firing searches, so two can overlap and resolve out of order. Only the most
    // recent request may write to the list.
    const latestRequestId = useRef(0);

    const debouncedSearchTerm = useDebounce(search.term, debounceTimeout);

    useEffect(searchForDrivers, [debouncedSearchTerm]);

    // The ids are held in the page's search state, so the remount that follows a change of driver does
    // not look them up again.
    const { blockedReason } = useRestrictedAccountIds({
        knownIds: search.restrictedAccountIds,
        onLoaded: onRestrictedAccountsLoaded
    });

    function onRestrictedAccountsLoaded(restrictedAccountIds) {
        onSearchChange({ restrictedAccountIds: restrictedAccountIds });
    }

    function searchForDrivers() {
        const trimmedTerm = debouncedSearchTerm.trim();

        if (trimmedTerm.length < minSearchLength) {
            // A search still in flight for a longer term would otherwise repopulate the list just cleared.
            latestRequestId.current += 1;
            setSearching(false);
            onSearchChange({ searchedTerm: null, results: defaultArray, totalCount: 0, pageNumber: firstPage, hasSearched: false });
            return;
        }

        // This component unmounts while a driver is selected, so without this the list the operator
        // came back to would be fetched again on every remount.
        if (trimmedTerm === search.searchedTerm) {
            return;
        }

        fetchPage(trimmedTerm, firstPage, search.pageSize);
    }

    function fetchPage(term, pageNumber, pageSize) {
        setSearching(true);

        const requestId = latestRequestId.current + 1;
        latestRequestId.current = requestId;

        GlobalSearchService.search(employeeEntityType, pageSize, pageNumber, term).then((response) => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            onSearchChange({
                searchedTerm: term,
                results: response?.data ?? defaultArray,
                totalCount: response?.totalCount ?? 0,
                pageNumber: pageNumber,
                pageSize: pageSize,
                hasSearched: true
            });
        }, () => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            NotificationToaster.show(Intent.DANGER, searchErrorMessage);
        }).finally(() => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            setSearching(false);
        });
    }

    function onSearchTermChange(event) {
        onSearchChange({ term: event.target.value });
    }

    function onPagingChange(newPageNumber, newPageSize) {
        fetchPage(search.searchedTerm, newPageNumber, newPageSize);
    }

    function selectResult(result) {
        onDriverSelect({
            employeeId: result.entityId,
            accountId: result.accountId,
            name: result.fullName,
            reference: result.reference,
            cardNumber: result.cardNumber,
            accountName: result.accountName,
            memberNumber: result.memberNumber,
            locationName: result.location?.name ?? null,
            statusName: result.status?.name ?? null,
            isAgency: result.type?.agency === true,
            statusTooltip: buildStatusTooltip(result)
        });
    }

    function buildTableRows() {
        return search.results.map((result) => ([
            (
                <div className="inline-items no-spacing" key="driver">
                    <EmployeeCircleStatus
                        status={result.status?.name}
                        tooltip={buildStatusTooltip(result)}
                        isAgency={result.type?.agency === true}
                    />
                    <span className="break-word">{result.fullName}</span>
                </div>
            ),
            // A reference is free text and a card number is long: without breaking them a single
            // unspaced value widens the table until the page scrolls sideways.
            (<span className="break-word" key="reference">{result.reference}</span>),
            (<span className="break-word" key="card">{result.cardNumber}</span>),
            `${result.accountName} - ${result.memberNumber}`,
            result.location?.name,
            buildSelectButton(result)
        ]));
    }

    function buildSelectButton(result) {
        const resultBlockedReason = blockedReason(result.accountId);

        const button = (
            <Button
                id={`select-driver-${result.accountId}-${result.entityId}`}
                text="Select"
                intent="primary"
                large={false}
                disabled={disabled || resultBlockedReason != null}
                onClick={() => selectResult(result)}
            />
        );

        if (resultBlockedReason == null) {
            return (<Fragment key="select">{button}</Fragment>);
        }

        return (
            <Tooltip key="select" content={resultBlockedReason}>
                {button}
            </Tooltip>
        );
    }

    return (
        <Fragment>
            <FormTextInput
                id="rules-engine-driver-search"
                placeholder="Search for a driver by name, reference or card number"
                value={search.term}
                onChange={onSearchTermChange}
                disabled={disabled}
                large
                wide
                icon="search"
            />

            <ShowHide
                evaluator={search.hasSearched || searching}
                show={(
                    <div className="spacer-bottom">
                        <ListingTable
                            id="listing-table-rules-engine-drivers"
                            headers={tableHeaders}
                            data={buildTableRows()}
                            totalRecordCount={search.totalCount}
                            loadingData={searching}
                            startPage={search.pageNumber}
                            pageSize={search.pageSize}
                            onPagingChange={onPagingChange}
                            noDataHeading={noResultsHeading}
                            noDataMessage={noResultsMessage}
                        />
                    </div>
                )}
            />
        </Fragment>
    );
}

RulesEngineEmployeeSearch.propTypes = {
    search: PropTypes.object,
    disabled: PropTypes.bool,
    onSearchChange: PropTypes.func,
    onDriverSelect: PropTypes.func
};
